// GovernanceTab.js — Governance & Operations tab for portfolio analytics
import React from 'react';
import { Shield, AlertTriangle, Calendar, FileText, Users } from 'lucide-react';
import { getStatusStyle } from '../../config/statusColors';
import {
  IPS_COMPLIANCE,
  MANDATE_LIMITS,
  COMMITTEE_SCHEDULE,
  AUDIT_TRAIL,
  REGULATORY_REPORTS,
  SIGNATORY_AUTHORITY,
} from '../../data/portfolioMockData';

// ─── Status badge helper ────────────────────────────────────────────────────────

const STATUS_STYLES = {
  compliant:    { backgroundColor: '#CDF3FC', color: '#1A7A8A' },
  OK:           { backgroundColor: '#CDF3FC', color: '#1A7A8A' },
  completed:    { backgroundColor: '#CDF3FC', color: '#1A7A8A' },
  warning:      { backgroundColor: '#FFF9D5', color: '#7A6B1A' },
  Warning:      { backgroundColor: '#FFF9D5', color: '#7A6B1A' },
  breach:       { backgroundColor: '#FFC1DA', color: '#8B2252' },
  upcoming:     { backgroundColor: '#e0e4ff', color: '#3832e3' },
  scheduled:    { backgroundColor: '#f4f4f5', color: '#52525b' },
  pending:      { backgroundColor: '#e4e4e7', color: '#3f3f46' },
  in_progress:  { backgroundColor: '#e0e4ff', color: '#3832e3' },
};

function statusBadge(status) {
  const style = STATUS_STYLES[status] || { backgroundColor: '#f4f4f5', color: '#52525b' };
  return (
    <span
      className="inline-block px-3 py-1 text-xs font-semibold font-mono uppercase tracking-wider rounded-none"
      style={style}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

// ─── Category badge (for audit trail) ───────────────────────────────────────────

const CATEGORY_STYLES = {
  'Foreign Exchange': { backgroundColor: '#e0e4ff', color: '#3832e3' },
  Settlement:  { backgroundColor: '#CDF3FC', color: '#1A7A8A' },
  Risk:        { backgroundColor: '#FFF9D5', color: '#7A6B1A' },
  Trading:     { backgroundColor: '#eef1ff', color: '#2f29c7' },
  Reporting:   { backgroundColor: '#f4f4f5', color: '#52525b' },
  Operations:  { backgroundColor: '#e4e4e7', color: '#3f3f46' },
  Compliance:  { backgroundColor: '#d4d4d8', color: '#27272a' },
};

function categoryBadge(category) {
  const style = CATEGORY_STYLES[category] || { backgroundColor: '#f4f4f5', color: '#52525b' };
  return (
    <span
      className="inline-block px-2 py-0.5 text-xs font-mono font-medium rounded-none"
      style={style}
    >
      {category}
    </span>
  );
}

// ─── Section wrapper ────────────────────────────────────────────────────────────

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-none border border-zinc-200 p-5 mt-6">
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className="w-4 h-4 text-zinc-500" />}
      <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400">{title}</h3>
    </div>
    {children}
  </div>
);

// ─── Component ──────────────────────────────────────────────────────────────────

const GovernanceTab = ({ role, governance }) => {
  const ips = governance?.ipsCompliance || IPS_COMPLIANCE;
  const mandates = governance?.mandateLimits || MANDATE_LIMITS;
  const committees = governance?.committeeSchedule || COMMITTEE_SCHEDULE;
  const auditTrail = governance?.auditTrail || AUDIT_TRAIL;
  const regulatory = governance?.regulatoryReports || REGULATORY_REPORTS;
  const signatories = governance?.signatoryAuthority || SIGNATORY_AUTHORITY;

  const isAdmin = role === 'investor_admin';

  // Summary card data
  const limitsOk = mandates.filter(m => m.status === 'OK').length;
  const limitsWarning = mandates.filter(m => m.status !== 'OK').length;
  const nextCommittee = committees.find(c => c.status === 'upcoming' || c.status === 'scheduled');
  const pendingFilings = regulatory.filter(r => r.status === 'pending' || r.status === 'in_progress').length;

  const summaryCards = [
    { title: 'IPS Status', value: ips.status, Icon: Shield },
    { title: 'Mandate Limits', value: `${limitsOk} OK / ${limitsWarning} Warning`, Icon: AlertTriangle },
    { title: 'Next Committee', value: nextCommittee ? new Date(nextCommittee.date).toLocaleDateString() : '—', Icon: Calendar },
    { title: 'Pending Filings', value: pendingFilings, Icon: FileText },
  ];

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {summaryCards.map(card => (
          <div
            key={card.title}
            className="bg-white rounded-none border border-zinc-200 p-5 hover:border-[#4D4D4D] transition-all"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400">
                  {card.title}
                </p>
                <p className="text-2xl font-bold font-sans text-black mt-1 capitalize">
                  {card.value}
                </p>
              </div>
              <div className="p-3 border border-zinc-300 bg-zinc-50 rounded-none">
                <card.Icon className="w-5 h-5 text-black" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* IPS Compliance */}
      <Section title="Investment Policy Statement (IPS) Compliance" icon={Shield}>
        <div className="flex items-center gap-4 mb-4 text-xs font-mono text-zinc-500">
          <span>Last Review: {ips.lastReviewDate}</span>
          <span>Next Review: {ips.nextReviewDate}</span>
          <span>Overall: {statusBadge(ips.status)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                <th className="text-left px-4 py-2">Objective</th>
                <th className="text-left px-4 py-2">Target</th>
                <th className="text-left px-4 py-2">Actual</th>
                <th className="text-center px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {ips.objectives.map(obj => (
                <tr key={obj.name} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-black">{obj.name}</td>
                  <td className="px-4 py-3 font-mono text-zinc-600 text-xs">{obj.target}</td>
                  <td className="px-4 py-3 font-mono text-black text-xs">{obj.actual}</td>
                  <td className="px-4 py-3 text-center">{statusBadge(obj.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Mandate Limits */}
      <Section title="Mandate Limits" icon={AlertTriangle}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                <th className="text-left px-4 py-2">Limit Name</th>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-left px-4 py-2">Limit</th>
                <th className="text-left px-4 py-2">Current</th>
                <th className="text-center px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {mandates.map(m => (
                <tr key={m.name} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-black">{m.name}</td>
                  <td className="px-4 py-3 text-zinc-500">{m.category}</td>
                  <td className="px-4 py-3 font-mono text-zinc-600 text-xs">{m.limit}</td>
                  <td className="px-4 py-3 font-mono text-black text-xs">{m.current}</td>
                  <td className="px-4 py-3 text-center">{statusBadge(m.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Committee Schedule — admin only */}
      {isAdmin && (
        <Section title="Committee Schedule" icon={Calendar}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-center px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Attendees</th>
                  <th className="text-left px-4 py-2">Decisions / Agenda</th>
                </tr>
              </thead>
              <tbody>
                {committees.map((c, idx) => (
                  <tr key={idx} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-zinc-700">
                      {new Date(c.date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 font-medium text-black">{c.type}</td>
                    <td className="px-4 py-3 text-center">{statusBadge(c.status)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{c.attendees || '—'}</td>
                    <td className="px-4 py-3 text-zinc-600 text-xs max-w-xs">{c.decisions || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Audit Trail — admin only */}
      {isAdmin && (
        <Section title="Audit Trail" icon={FileText}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                  <th className="text-left px-4 py-2">Timestamp</th>
                  <th className="text-left px-4 py-2">User</th>
                  <th className="text-left px-4 py-2">Action</th>
                  <th className="text-left px-4 py-2">Category</th>
                  <th className="text-left px-4 py-2">Entity</th>
                </tr>
              </thead>
              <tbody>
                {auditTrail.map((entry, idx) => (
                  <tr key={idx} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500 whitespace-nowrap">
                      {new Date(entry.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-medium text-black text-xs">{entry.user}</td>
                    <td className="px-4 py-3 text-zinc-700 text-xs max-w-sm">{entry.action}</td>
                    <td className="px-4 py-3">{categoryBadge(entry.category)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500">{entry.entity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Regulatory Reports */}
      <Section title="Regulatory Reports" icon={FileText}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                <th className="text-left px-4 py-2">Report</th>
                <th className="text-left px-4 py-2">Jurisdiction</th>
                <th className="text-left px-4 py-2">Due Date</th>
                <th className="text-center px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Last Filed</th>
              </tr>
            </thead>
            <tbody>
              {regulatory.map(r => (
                <tr key={r.name} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-black">{r.name}</td>
                  <td className="px-4 py-3 text-zinc-600">{r.jurisdiction}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">{r.dueDate}</td>
                  <td className="px-4 py-3 text-center">{statusBadge(r.status)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{r.lastFiled}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Signatory Authority — admin only */}
      {isAdmin && (
        <Section title="Signatory Authority" icon={Users}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-center px-4 py-2">Tier</th>
                  <th className="text-right px-4 py-2">Limit</th>
                  <th className="text-left px-4 py-2">Approval Scope</th>
                </tr>
              </thead>
              <tbody>
                {signatories.map(s => {
                  const tierStyle = s.tier === 'Tier 1'
                    ? { backgroundColor: '#eef1ff', color: '#2f29c7' }
                    : s.tier === 'Tier 2'
                    ? { backgroundColor: '#e4e4e7', color: '#3f3f46' }
                    : { backgroundColor: '#f4f4f5', color: '#52525b' };

                  return (
                    <tr key={s.name} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-black">{s.name}</td>
                      <td className="px-4 py-3 text-zinc-600">{s.role}</td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className="inline-block px-3 py-1 text-xs font-semibold font-mono rounded-none"
                          style={tierStyle}
                        >
                          {s.tier}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs">{s.limit}</td>
                      <td className="px-4 py-3 text-zinc-600 text-xs">{s.approvalScope}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
};

export default GovernanceTab;
